import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import SurveysModal from "../components/SurveysModal";
import DogEditModal from "../components/DogEditModal";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { buildPatchPayload } from "../utils/dogEditForm";

const Page = styled.div`
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(135deg, #fff7ed, #fff9f2);
`;

const Title = styled.h2`
  margin-bottom: 12px;
  color: #1f2937;
  font-size: 24px;
  font-weight: 700;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fffdf9;
  border-radius: 10px;
  overflow: hidden;
`;

const Th = styled.th`
  background: #ffedd5;
  padding: 12px;
  text-align: left;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  background: #6b7280;
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #4b5563;
  }
`;

const SecondaryButton = styled.button`
  background: linear-gradient(90deg, #ff7a2a, #ffb06b);
  color: #fff;
  padding: 8px 16px;
  border-radius: 10px;
  border: none;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
`;

const ExportButton = styled.button`
  background: #059669;
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  &:hover {
    background: #047857;
  }
  &:active {
    background: #065f46;
  }
`;

const DogsContainer = styled.div`
  margin-top: 8px;
`;

const DogsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 16px;
`;

const DogCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f3f4f6;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  display: flex;
  gap: 20px;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageCarouselContainer = styled.div`
  position: relative;
  min-width: 300px;
  width: 300px;
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  background: #f9fafb;
  border: 2px solid #ffedd5;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: ${(props) => (props.active ? "block" : "none")};
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  z-index: 10;
  transition: background 0.2s;
  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  ${(props) => (props.direction === "prev" ? "left: 10px;" : "right: 10px;")}
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
`;

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${(props) => (props.active ? "#c2410c" : "#d1d5db")};
  cursor: pointer;
  padding: 0;
  transition: background 0.2s;
  &:hover {
    background: ${(props) => (props.active ? "#c2410c" : "#9ca3af")};
  }
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DogCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 2px solid #ffedd5;
`;

const DogTitle = styled.h3`
  margin: 0;
  color: #c2410c;
  font-size: 18px;
  font-weight: 700;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => {
    if (props.status === "ACTIVE") return "#d1fae5";
    return "#fee2e2";
  }};
  color: ${(props) => {
    if (props.status === "ACTIVE") return "#065f46";
    return "#991b1b";
  }};
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
`;

const Section = styled.div`
  margin-bottom: 0;
`;

const SectionTitle = styled.h4`
  margin: 0 0 8px 0;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
  color: #6b7280;
`;

const InfoLabel = styled.span`
  font-weight: 500;
  color: #4b5563;
`;

const InfoValue = styled.span`
  color: #1f2937;
  font-weight: 600;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const Tag = styled.span`
  background: #f3f4f6;
  color: #4b5563;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
`;

const NoImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f3f4f6;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
  font-size: 16px;
`;

const EmptyText = styled.div`
  text-align: center;
  padding: 40px;
  color: #9ca3af;
  font-size: 16px;
`;

const EditButton = styled.button`
  background: #c2410c;
  color: #fff;
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  flex-shrink: 0;
  transition: background 0.2s;
  &:hover {
    background: #9a3412;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const formatValue = (value) => {
  if (value === null || value === undefined) return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ") || "None";
  return String(value);
};

const SEX_CODE_MAP = { MALE: "M", FEMALE: "F", UNKNOWN: "U" };
const AGE_CODE_MAP = { PUP: "P", YOUNG: "Y", ADULT: "A", OLD: "O" };
const SIZE_CODE_MAP = { SMALL: "S", MEDIUM: "M", LARGE: "L" };
const PATTERN_CODE_MAP = {
  SOLID: "S",
  TRICOLOR: "TC",
  TIGER: "T",
  ZEBRA: "Z",
  MIXED: "BC",
};
const REPRO_CODE_MAP = {
  PREGNANT: "PREG",
  IN_HEAT: "HEAT",
  LACTATING: "LAC",
  NA: "NA",
};
const CONDITION_CODE_MAP = { POOR: "P", FAIR: "F", GOOD: "G" };

const csvEscape = (val) => {
  const s = val == null ? "" : String(val);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const getCollarFlag = (identification) => {
  if (Array.isArray(identification)) {
    return identification.includes("COLLAR") ? "Y" : "N";
  }
  if (typeof identification === "string") {
    return identification === "COLLAR" ? "Y" : "N";
  }
  if (identification && typeof identification === "object") {
    return identification.collarStatus === "COLLAR" ? "Y" : "N";
  }
  return "N";
};

const getNotchFlag = (dog) => {
  const f = dog?.fields || {};
  const val = dog?.notch_type_identication ?? f.notch_type_identication;
  return val === true ? "Y" : "N";
};

const buildMasterFileCsvRows = (dogs) => {
  const headers = [
    "DogID",
    "SEX",
    "AGE",
    "SIZE",
    "PATTERN",
    "Primary colour",
    "Secondary colour",
    "NTC",
    "Rep/STATUS",
    "COLLAR (Y/N)",
    "CONDITION",
    "LAT",
    "LONG",
  ];

  const rows = dogs.map((dog) => {
    const f = dog.fields || {};
    const demo = f.demographics || {};
    const app = f.appearance || {};
    const body = f.body || {};
    const repro = f.reproductiveStatus ?? dog.reproductiveStatus ?? "";

    return [
      f.dogUID || "",
      SEX_CODE_MAP[demo.gender] || "",
      AGE_CODE_MAP[demo.ageGroup] || "",
      SIZE_CODE_MAP[body.size] || "",
      PATTERN_CODE_MAP[app.coatPattern] || "",
      app.primaryColor || "",
      app.secondaryColor || "",
      getNotchFlag(dog),
      REPRO_CODE_MAP[repro] || "",
      getCollarFlag(f.identification),
      CONDITION_CODE_MAP[body.bodyCondition] || "",
      dog.coords?.latitude ?? dog.homeCentroid?.latitude ?? "",
      dog.coords?.longitude ?? dog.homeCentroid?.longitude ?? "",
    ];
  });

  return [
    headers.map(csvEscape).join(","),
    ...rows.map((row) => row.map(csvEscape).join(",")),
  ].join("\n");
};

const downloadCsv = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ObservationImageCarousel = ({ images, label }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <ImageCarouselContainer>
        <CarouselWrapper>
          <NoImagePlaceholder>No images available</NoImagePlaceholder>
        </CarouselWrapper>
      </ImageCarouselContainer>
    );
  }

  return (
    <ImageCarouselContainer>
      <CarouselWrapper>
        {images.map((url, index) => (
          <CarouselImage
            key={index}
            src={url}
            alt={`${label || "Dog"} — ${index + 1}`}
            active={index === currentIndex}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ))}
        {images.length > 1 && (
          <>
            <CarouselButton
              direction="prev"
              onClick={() =>
                setCurrentIndex((prev) =>
                  prev === 0 ? images.length - 1 : prev - 1,
                )
              }
            >
              ‹
            </CarouselButton>
            <CarouselButton
              direction="next"
              onClick={() =>
                setCurrentIndex((prev) =>
                  prev === images.length - 1 ? 0 : prev + 1,
                )
              }
            >
              ›
            </CarouselButton>
          </>
        )}
      </CarouselWrapper>
      {images.length > 1 && (
        <CarouselDots>
          {images.map((_, index) => (
            <Dot
              key={index}
              active={index === currentIndex}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Image ${index + 1}`}
              type="button"
            />
          ))}
        </CarouselDots>
      )}
    </ImageCarouselContainer>
  );
};

const HistoryDogRow = ({ dog, onEdit }) => {
  const uid = dog.fields?.dogUID || dog._id?.slice?.(-8) || "—";
  const coords = dog.coords;
  const coordStr =
    coords?.latitude != null && coords?.longitude != null
      ? `${Number(coords.latitude).toFixed(5)}, ${Number(coords.longitude).toFixed(5)}`
      : "N/A";

  return (
    <DogCard>
      <ObservationImageCarousel
        images={dog.uploads || []}
        label={uid}
      />
      <CardContent>
        <DogCardHeader>
          <DogTitle>{uid}</DogTitle>
          <HeaderActions>
            {dog.status ? (
              <StatusBadge status={dog.status}>{dog.status}</StatusBadge>
            ) : null}
            <EditButton type="button" onClick={() => onEdit(dog)}>
              Edit
            </EditButton>
          </HeaderActions>
        </DogCardHeader>
        <DetailsGrid>
          <Section>
            <SectionTitle>Demographics</SectionTitle>
            <InfoRow>
              <InfoLabel>Age Group:</InfoLabel>
              <InfoValue>
                {formatValue(dog.fields?.demographics?.ageGroup)}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Gender:</InfoLabel>
              <InfoValue>
                {formatValue(dog.fields?.demographics?.gender)}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Breed:</InfoLabel>
              <InfoValue>
                {formatValue(dog.fields?.demographics?.breed)}
                {dog.fields?.demographics?.breedOther &&
                  ` (${dog.fields.demographics.breedOther})`}
              </InfoValue>
            </InfoRow>
          </Section>
          <Section>
            <SectionTitle>Appearance</SectionTitle>
            <InfoRow>
              <InfoLabel>Primary Color:</InfoLabel>
              <InfoValue>
                {formatValue(dog.fields?.appearance?.primaryColor)}
                {dog.fields?.appearance?.primaryColorOther &&
                  ` (${dog.fields.appearance.primaryColorOther})`}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Secondary Color:</InfoLabel>
              <InfoValue>
                {formatValue(dog.fields?.appearance?.secondaryColor)}
                {dog.fields?.appearance?.secondaryColorOther &&
                  ` (${dog.fields.appearance.secondaryColorOther})`}
              </InfoValue>
            </InfoRow>
          </Section>
          <Section>
            <SectionTitle>Body</SectionTitle>
            <InfoRow>
              <InfoLabel>Size:</InfoLabel>
              <InfoValue>{formatValue(dog.fields?.body?.size)}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Body Condition:</InfoLabel>
              <InfoValue>
                {formatValue(dog.fields?.body?.bodyCondition)}
              </InfoValue>
            </InfoRow>
          </Section>
          <Section>
            <SectionTitle>Other</SectionTitle>
            <InfoRow>
              <InfoLabel>Reproductive Status:</InfoLabel>
              <InfoValue>
                {formatValue(
                  dog.fields?.reproductiveStatus ?? dog.reproductiveStatus,
                )}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Observed at:</InfoLabel>
              <InfoValue>{coordStr}</InfoValue>
            </InfoRow>
            {dog.fields?.remarks ? (
              <InfoRow>
                <InfoLabel>Remarks:</InfoLabel>
                <InfoValue>{formatValue(dog.fields.remarks)}</InfoValue>
              </InfoRow>
            ) : null}
            {dog.fields?.scars?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <InfoLabel>Scars:</InfoLabel>
                <TagsContainer>
                  {dog.fields.scars.map((scar, idx) => (
                    <Tag key={idx}>{scar}</Tag>
                  ))}
                </TagsContainer>
              </div>
            )}
            {dog.fields?.spots?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <InfoLabel>Spots:</InfoLabel>
                <TagsContainer>
                  {dog.fields.spots.map((spot, idx) => (
                    <Tag key={idx}>{spot}</Tag>
                  ))}
                </TagsContainer>
              </div>
            )}
            {dog.fields?.injuries?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <InfoLabel>Injuries:</InfoLabel>
                <TagsContainer>
                  {dog.fields.injuries.map((injury, idx) => (
                    <Tag key={idx}>{injury}</Tag>
                  ))}
                </TagsContainer>
              </div>
            )}
          </Section>
        </DetailsGrid>
      </CardContent>
    </DogCard>
  );
};

const History = () => {
  const [routes, setRoutes] = useState([]);
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [historyDogs, setHistoryDogs] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [editModalObservation, setEditModalObservation] = useState(null);
  const [savingObservation, setSavingObservation] = useState(false);
  const [patchError, setPatchError] = useState(null);
  const { token } = useAuth();

  const fetchRoutes = useCallback(async () => {
    try {
      setLoadingRoutes(true);
      const res = await api.get("/api/dataanalysis/routes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.routes || res.data?.data || res.data || [];
      setRoutes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching routes:", err);
    } finally {
      setLoadingRoutes(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const fetchHistory = useCallback(async () => {
    if (!selectedSurveyId) {
      setHistoryDogs([]);
      return;
    }
    try {
      setLoadingHistory(true);
      const res = await api.get("/api/dog-observations/history", {
        params: { surveyId: selectedSurveyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.data ?? res.data?.observations ?? res.data ?? [];
      setHistoryDogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching observation history:", err);
      setHistoryDogs([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [selectedSurveyId, token]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleRouteRowClick = (routeId) => {
    setSelectedRouteId(routeId);
    setIsSurveyModalOpen(true);
  };

  const handleSurveyModalClose = () => {
    setIsSurveyModalOpen(false);
  };

  const handleSurveySelect = (surveyId) => {
    setSelectedSurveyId(surveyId);
    setIsSurveyModalOpen(false);
  };

  const handleBackToRoutes = () => {
    setSelectedSurveyId(null);
    setSelectedRouteId(null);
    setHistoryDogs([]);
  };

  const openEditModal = (dog) => {
    setPatchError(null);
    setEditModalObservation(dog);
  };

  const closeEditModal = () => {
    if (savingObservation) return;
    setEditModalObservation(null);
    setPatchError(null);
  };

  const saveObservationPatch = async (form) => {
    if (!editModalObservation) return;
    setSavingObservation(true);
    setPatchError(null);
    try {
      const payload = buildPatchPayload(form);
      const res = await api.patch(
        `/api/dog-observations/${editModalObservation._id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data?.data;
      if (updated) {
        setHistoryDogs((prev) =>
          prev.map((dog) => (dog._id === updated._id ? updated : dog)),
        );
      }
      setEditModalObservation(null);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to save observation";
      setPatchError(msg);
    } finally {
      setSavingObservation(false);
    }
  };

  const exportHistoryToCSV = () => {
    if (!historyDogs || historyDogs.length === 0) {
      alert("No dogs data to export");
      return;
    }

    const routeName =
      routes.find((r) => r._id === selectedRouteId)?.name || "export";
    const date = new Date().toISOString().split("T")[0];
    const filename = `${routeName}_survey_${selectedSurveyId}_history_${date}.csv`;

    downloadCsv(buildMasterFileCsvRows(historyDogs), filename);
  };

  return (
    <Page>
      <Title>History</Title>

      {!selectedSurveyId ? (
        loadingRoutes ? (
          <LoadingText>Loading routes…</LoadingText>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Total Surveys</Th>
                <Th>Start Location</Th>
                <Th>End Location</Th>
              </tr>
            </thead>
            <tbody>
              {routes.length === 0 ? (
                <tr>
                  <Td colSpan={4} style={{ textAlign: "center", padding: 20 }}>
                    No routes found.
                  </Td>
                </tr>
              ) : (
                routes.map((r) => (
                  <tr
                    key={r._id}
                    onClick={() => handleRouteRowClick(r._id)}
                    style={{ cursor: "pointer" }}
                  >
                    <Td>{r.name}</Td>
                    <Td>{r.surveyIds?.length ?? 0}</Td>
                    <Td>
                      {r.startLocation?.latitude?.toFixed(4)},{" "}
                      {r.startLocation?.longitude?.toFixed(4)}
                    </Td>
                    <Td>
                      {r.endLocation?.latitude?.toFixed(4)},{" "}
                      {r.endLocation?.longitude?.toFixed(4)}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )
      ) : (
        <>
          <Toolbar>
            <BackButton type="button" onClick={handleBackToRoutes}>
              ← Back to routes
            </BackButton>
            <SecondaryButton
              type="button"
              onClick={() => setIsSurveyModalOpen(true)}
            >
              Change survey
            </SecondaryButton>
            {!loadingHistory && historyDogs.length > 0 && (
              <ExportButton type="button" onClick={exportHistoryToCSV}>
                📥 Export Dogs
              </ExportButton>
            )}
          </Toolbar>
          {loadingHistory ? (
            <LoadingText>Loading history…</LoadingText>
          ) : historyDogs.length === 0 ? (
            <EmptyText>No history records for this survey.</EmptyText>
          ) : (
            <DogsContainer>
              <DogsGrid>
                {historyDogs.map((dog) => (
                  <HistoryDogRow
                    key={dog._id}
                    dog={dog}
                    onEdit={openEditModal}
                  />
                ))}
              </DogsGrid>
            </DogsContainer>
          )}
        </>
      )}

      {isSurveyModalOpen && selectedRouteId && (
        <SurveysModal
          isOpen={isSurveyModalOpen}
          onClose={handleSurveyModalClose}
          routeId={selectedRouteId}
          selectable={true}
          onSurveySelect={handleSurveySelect}
        />
      )}

      {editModalObservation && (
        <DogEditModal
          dog={editModalObservation}
          title={`Edit observation — ${editModalObservation.fields?.dogUID || editModalObservation._id}`}
          saving={savingObservation}
          error={patchError}
          onClose={closeEditModal}
          onSave={saveObservationPatch}
        />
      )}
    </Page>
  );
};

export default History;
